import { Button, Chip, TabNav, type TabNavItem, TopBar, cn } from '@studio/ui';
import { Star } from 'lucide-react';
import type { ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Logo } from './Logo';
import { ThemeToggle } from './ThemeToggle';

/**
 * Opinionated app shell — TopBar + TabNav + scrollable main. Used by the
 * top-level surfaces (Products / Gallery, etc.). Surface 4 of the UI vision
 * (top-header actions: Brand button + Profile button) lives here.
 *
 * Brand/Profile buttons render as `disabled` placeholders until their
 * modals ship in PR 14 / PR 15+.
 */

const TABS: TabNavItem[] = [
  { id: 'products', label: 'Products' },
  { id: 'gallery', label: 'Gallery' },
  { id: 'connections', label: 'Connections' },
];

export interface AppShellProps {
  /** Active tab id; matches a member of `TABS`. */
  activeTab: 'products' | 'gallery' | 'connections';
  children: ReactNode;
  /** Optional credit balance. Hidden when undefined. */
  credits?: { balance: number; planLabel?: string };
  /** Hook for opening the Brand DNA modal. */
  onOpenBrand?: () => void;
  /** Hook for opening the Profile / settings modal. */
  onOpenProfile?: () => void;
  className?: string;
}

export function AppShell({
  activeTab,
  children,
  credits,
  onOpenBrand,
  onOpenProfile,
  className,
}: AppShellProps) {
  const navigate = useNavigate();
  const location = useLocation();

  function handleTabChange(id: string): void {
    if (id === 'products' && location.pathname !== '/products') navigate('/products');
    else if (id === 'gallery' && location.pathname !== '/gallery') navigate('/gallery');
    else if (id === 'connections' && location.pathname !== '/connections') navigate('/connections');
  }

  return (
    <div className={cn('flex min-h-dvh flex-col bg-bg text-fg', className)}>
      <TopBar
        left={<Logo />}
        right={
          <>
            <Button
              variant="ghost"
              size="sm"
              onClick={onOpenBrand}
              disabled={!onOpenBrand}
              aria-label="Open brand"
            >
              Brand
            </Button>
            {credits ? (
              <Chip variant="solid" size="md" className="gap-1.5">
                <Star className="h-3.5 w-3.5 text-brand" fill="currentColor" />
                <span>{credits.balance.toLocaleString()} credits</span>
                {credits.planLabel ? (
                  <span className="text-fg-3">· {credits.planLabel}</span>
                ) : null}
              </Chip>
            ) : null}
            <ThemeToggle />
            <button
              type="button"
              aria-label="Open profile"
              onClick={onOpenProfile}
              disabled={!onOpenProfile}
              className="h-7 w-7 rounded-pill border border-border bg-surface-2 transition-colors hover:bg-surface-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring disabled:cursor-not-allowed disabled:opacity-60"
            />
          </>
        }
      />
      <div className="border-b border-border-subtle px-6">
        <TabNav items={TABS} activeId={activeTab} onChange={handleTabChange} />
      </div>
      <main
        id={`tabpanel-${activeTab}`}
        role="tabpanel"
        aria-labelledby={`tab-${activeTab}`}
        className="mx-auto w-full max-w-7xl flex-1 px-6 py-8"
      >
        {children}
      </main>
    </div>
  );
}
